import React from "react"
import { useStyletron } from "baseui"
import { Grid, Cell } from "baseui/layout-grid"
import { ProgressBar } from "./Progress"
import { Checkbox } from "./Checkbox"
import { Person } from "../App"
import { Heading, HeadingLevel } from "baseui/heading"
import { useLocation, useHistory } from "react-router-dom"
import { findBranch } from "../util"
import { getConstant } from "../constants"

export const Layout = ({
  children,
  heading,
  sectionStep,
  person,
  setPerson,
  volunteerOnly,
  scrollTrigger
}: {
  children: React.ReactNode
  heading: string
  person: Person
  setPerson?: (person: Person) => void
  sectionStep: number
  volunteerOnly?: boolean
  scrollTrigger?: number
}) => {
  const [css] = useStyletron()
  const progressRef = React.useRef()
  const { pathname } = useLocation()

  React.useEffect(() => {
    if (
      scrollTrigger &&
      progressRef &&
      progressRef.current &&
      ((progressRef.current as unknown) as HTMLElement).scrollIntoView
    ) {
      ;((progressRef.current as unknown) as HTMLElement).scrollIntoView(true)
    }
  }, [pathname, scrollTrigger])
  const branch = findBranch(person)
  const { push } = useHistory()
  let sections = [
    {
      label: "Personal details",
      url: "/"
    }
  ]
  if (person.join) {
    sections.push({
      label: "Work details",
      url: person.last_work_details_path || "/employment-details"
    })
    sections.push({
      label: "Your Union",
      url: "/union"
    })
    if (!branch || branch.payment_mode !== "bypass") {
      sections.push({
        label: "Payment options",
        url: "/payment"
      })
    }
  }
  if (person.volunteer) {
    sections.push({
      label: "Volunteer details",
      url: "/volunteer-details"
    })
  }
  sections.push({
    label: "Finish",
    url: "/complete"
  })
  // For coverage lookup, replace the whole sections list with a single page
  if (getConstant("coverageOnly")) {
    sections = [
      {
        label: "Coverage lookup",
        url: "/coverage"
      }
    ]
    heading = "Coverage Lookup"
    if (pathname !== "/coverage" && pathname !== "/manual-coverage") {
      push(`/coverage`)
    }
  }
  return (
    <HeadingLevel>
      <Grid gridMargins={[16, 24, 24]}>
        {sectionStep === 0 &&
          !volunteerOnly &&
          getConstant("volunteerEnabled") && (
            <Cell span={[4, 8, 12]}>
              <Checkbox
                name="join"
                onChange={e => {
                  if (setPerson) {
                    setPerson({ ...person, join: e.currentTarget.checked })
                  }
                }}
                checked={person.join}
              >
                I want to join my union
              </Checkbox>
              <Checkbox
                name="volunteer"
                onChange={e => {
                  if (setPerson) {
                    setPerson({ ...person, volunteer: e.currentTarget.checked })
                  }
                }}
                checked={person.volunteer}
              >
                I also want to volunteer on Australian Unions' campaigns
              </Checkbox>
            </Cell>
          )}
        {(person.join || person.volunteer) && (
          <>
            <Cell
              overrides={{ Cell: { props: { ref: progressRef } } }}
              span={[4, 8, 12]}
            >
              <ProgressBar sections={sections} sectionStep={sectionStep} />
            </Cell>
          </>
        )}
        <>
          <Cell span={[4, 8, 12]}>
            <HeadingLevel>
              <Heading
                overrides={{
                  Block: {
                    style: ({ $theme }) => ({
                      ...$theme.typography.HeadingXLarge,
                      marginTop: "16px",
                      marginBottom: "0",
                      marginLeft: "0s",
                      marginRight: "0"
                    })
                  }
                }}
              >
                {heading}
              </Heading>
              <hr
                className={css({
                  border: 0,
                  height: "2px",
                  backgroundColor: "#333333"
                })}
              />
            </HeadingLevel>
          </Cell>
        </>
      </Grid>
      <Grid gridMargins={[16, 24, 24]}>{children}</Grid>
    </HeadingLevel>
  )
}
